import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { authenticatedBitswanBackendInstance } from '@/lib/api-client';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Loader2, Users, ArrowRight, DollarSign } from 'lucide-react';
import OnboardingWizard, { useOnboarding } from '@/components/advisor/onboarding/OnboardingWizard';
import CertificationCard from '@/components/advisor/CertificationCard';
import TrialWatchCard from '@/components/advisor/TrialWatchCard';
import { money } from '@/types/offer';

interface Client {
  id: string;
  company_name: string;
  status: string;
  status_display: string;
  updated_at: string;
}



function StatCard({
  icon: Icon,
  label,
  value,
  description,
  loading,
}: {
  icon: React.ElementType;
  label: string;
  value: string;
  description: string;
  loading?: boolean;
}) {
  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between pb-2">
        <CardTitle className="text-sm font-medium text-muted-foreground">{label}</CardTitle>
        <Icon className="h-4 w-4 text-amber-500" />
      </CardHeader>
      <CardContent>
        {loading ? (
          <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
        ) : (
          <>
            <div className="text-2xl font-bold">{value}</div>
            <p className="text-xs text-muted-foreground mt-1">{description}</p>
          </>
        )}
      </CardContent>
    </Card>
  );
}

export default function DashboardPage() {
  const navigate = useNavigate();
  const { isOnboardingComplete, completeOnboarding } = useOnboarding();
  const [showOnboarding, setShowOnboarding] = useState(!isOnboardingComplete);

  // Add Customer dialog state

  const { data: clients, isLoading: loadingClients } = useQuery({
    queryKey: ['advisor-dashboard-clients'],
    queryFn: async () => {
      const api = await authenticatedBitswanBackendInstance();
      const res = await api.get('/advisor/clients/');
      return res.data as Client[];
    },
  });


  const activeClients = clients?.filter((c) => ['active', 'onboarded'].includes(c.status)).length ?? 0;


  const { data: recurring, isLoading: loadingRecurring } = useQuery<{
    currencies: Array<{ currency: string; monthly_total: string; contracts: number }>;
  }>({
    queryKey: ['advisor-recurring-revenue'],
    queryFn: async () => {
      const api = await authenticatedBitswanBackendInstance();
      const res = await api.get('/advisor/recurring-revenue/');
      return res.data;
    },
  });
  const earning = recurring?.currencies ?? [];
  const formattedMRR = earning.length
    ? earning.map((line) => money(line.monthly_total, line.currency)).join(' + ')
    : '—';
  const runningContracts = earning.reduce((sum, line) => sum + line.contracts, 0);

  // Recent activity: combine recent updates
  const recentItems: Array<{ type: string; id: string; clientId?: string; label: string; detail: string; date: string }> = [];
  clients?.slice(0, 3).forEach((c) => {
    recentItems.push({ type: 'client', id: c.id, label: c.company_name, detail: `Client · ${c.status_display || c.status}`, date: c.updated_at });
  });
  recentItems.sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime());

  const handleActivityClick = (item: typeof recentItems[number]) => {
    if (item.type === 'client') navigate(`/advisor/clients/${item.id}`);
  };

  return (
    <div className="space-y-6">
      {showOnboarding && (
        <OnboardingWizard onComplete={() => {
          completeOnboarding();
          setShowOnboarding(false);
        }} />
      )}

      <CertificationCard />

      <TrialWatchCard />

      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold tracking-tight">Dashboard</h2>
          <p className="text-muted-foreground">Overview of your advisory practice</p>
        </div>
        <Button variant="outline" onClick={() => navigate('/advisor/clients')} data-testid="dashboard-open-clients">
          Your clients <ArrowRight className="h-4 w-4 ml-2" />
        </Button>
      </div>

      {/* Stat Grid */}
      <div className="grid gap-4 sm:grid-cols-2" data-testid="stat-grid">
        <StatCard
          icon={Users}
          label="Active Clients"
          value={String(activeClients)}
          description={`${clients?.length ?? 0} total clients`}
          loading={loadingClients}
        />
        <StatCard
          icon={DollarSign}
          label="Monthly Recurring"
          value={formattedMRR}
          description={`${runningContracts} running contract${runningContracts !== 1 ? 's' : ''}`}
          loading={loadingRecurring}
        />
      </div>

      <div className="grid gap-4">
        <Card>
          <CardHeader>
            <CardTitle>Recent Activity</CardTitle>
            <CardDescription>Latest updates across your practice</CardDescription>
          </CardHeader>
          <CardContent>
            {recentItems.length === 0 ? (
              <p className="text-sm text-muted-foreground">
                No recent activity yet. Add a client to get started.
              </p>
            ) : (
              <div className="space-y-3" data-testid="recent-activity">
                {recentItems.slice(0, 6).map((item, i) => (
                  <div
                    key={i}
                    className="flex items-center justify-between gap-3 cursor-pointer hover:bg-muted/50 rounded-lg p-2 -m-2 transition-colors"
                    onClick={() => handleActivityClick(item)}
                    data-testid={`activity-item-${i}`}
                  >
                    <div className="min-w-0">
                      <p className="text-sm font-medium truncate hover:text-amber-600">{item.label}</p>
                      <p className="text-xs text-muted-foreground">{item.detail}</p>
                    </div>
                    <div className="flex items-center gap-2 shrink-0">
                      <span className="text-xs text-muted-foreground">
                        {new Date(item.date).toLocaleDateString()}
                      </span>
                      <ArrowRight className="h-3 w-3 text-muted-foreground/40" />
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
